import type { ReactNode } from 'react'

import { cn } from '../../lib/utils'
import { Icon } from '../icon'
import { Dot, type Tone } from './tone'

/**
 * The data table.
 *
 * Row rhythm comes from `--rowpad` and `--cellfs`, so the density switch moves every table in the
 * product at once and no screen carries its own spacing. That is the whole reason those two are
 * tokens rather than classes.
 *
 * The header is sticky against the scroll container, because a dense table whose header leaves the
 * screen is a grid of unlabelled numbers.
 */

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    // The wrapper scrolls, never the page. A wide table must not widen the document.
    <div className="overflow-x-auto">
      <table className={cn('w-full border-collapse text-left', className)}>{children}</table>
    </div>
  )
}

export function THead({ children }: { children: ReactNode }) {
  return (
    <thead className="sticky top-0 z-10">
      <tr>{children}</tr>
    </thead>
  )
}

export function TBody({ children }: { children?: ReactNode }) {
  return <tbody>{children}</tbody>
}

/**
 * A column heading.
 *
 * Plain case at 11.5px. When `sort` is given it becomes a real button, and the arrow only appears
 * on the active column — an arrow on every column tells you nothing about which one is sorted.
 */
export function Th({
  children,
  align = 'left',
  width,
  sort,
  className,
}: {
  children?: ReactNode
  align?: 'left' | 'center' | 'right'
  width?: number | string
  sort?: { active: boolean; direction: 'asc' | 'desc'; onSort: () => void }
  className?: string
}) {
  const alignment =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'

  return (
    <th
      scope="col"
      style={{ width }}
      className={cn(
        'bg-panel border-line text-muted border-b px-[14px] py-2 text-[11.5px] font-medium',
        alignment,
        className,
      )}
    >
      {sort ? (
        <button
          onClick={sort.onSort}
          className={cn(
            'hover:text-text inline-flex items-center gap-1 transition-colors',
            sort.active && 'text-text',
          )}
          aria-sort={sort.active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
          {children}
          {sort.active && (
            <Icon
              name="chevronDown"
              size={11}
              className={sort.direction === 'asc' ? 'rotate-180' : undefined}
            />
          )}
        </button>
      ) : (
        children
      )}
    </th>
  )
}

export function Td({
  children,
  align = 'left',
  mono,
  className,
}: {
  children?: ReactNode
  align?: 'left' | 'center' | 'right'
  /** For an address or a DNS record — 11.5px, the design's monospace size in a row. */
  mono?: boolean
  className?: string
}) {
  const alignment =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'

  return (
    <td
      className={cn(
        'border-line2 row-pad cell-text border-b px-[14px] align-middle',
        alignment,
        mono && 'font-mono text-[11.5px]',
        className,
      )}
    >
      {children}
    </td>
  )
}

/**
 * A row.
 *
 * `onOpen` makes it a real activation target — focusable, answering Enter and Space — rather than
 * an element that only responds to a mouse. A clickable row a keyboard cannot reach is a row half
 * the operators of an internal tool cannot use.
 */
export function Tr({
  children,
  onOpen,
  selected,
  className,
}: {
  children: ReactNode
  onOpen?: () => void
  selected?: boolean
  className?: string
}) {
  return (
    <tr
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onOpen()
              }
            }
          : undefined
      }
      tabIndex={onOpen ? 0 : undefined}
      role={onOpen ? 'button' : undefined}
      className={cn(
        'transition-colors',
        selected ? 'bg-sel' : 'hover:bg-hover',
        onOpen && 'cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2',
        className,
      )}
    >
      {children}
    </tr>
  )
}

/** A status cell: a 6px dot and the word, both in the tone. */
export function StatusCell({ tone, children }: { tone: Tone; children: ReactNode }) {
  const text =
    tone === 'ok'
      ? 'text-ok'
      : tone === 'warn'
        ? 'text-warn'
        : tone === 'bad'
          ? 'text-bad'
          : 'text-muted'

  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <Dot tone={tone} label="" size={6} />
      <span className={text}>{children}</span>
    </span>
  )
}

/**
 * A proportion, as a number over a 4px bar.
 *
 * The number is the fact and the bar is the shape of it — a bar alone cannot be read precisely and
 * a number alone cannot be scanned down a column.
 */
export function MeterCell({
  value,
  percent,
  tone = 'ok',
}: {
  value: ReactNode
  percent: number
  tone?: Tone
}) {
  const fill =
    tone === 'bad' ? 'bg-bad' : tone === 'warn' ? 'bg-warn' : tone === 'ok' ? 'bg-ok' : 'bg-faint'

  return (
    <div className="min-w-[110px]">
      <span className="text-text text-[11.5px]">{value}</span>
      <div className="bg-line2 mt-[5px] h-1 overflow-hidden rounded-[3px]">
        <div
          className={cn('h-full rounded-[3px]', fill)}
          style={{ width: `${Math.max(2, Math.min(100, percent))}%` }}
        />
      </div>
    </div>
  )
}
